import React, { useState } from 'react';
import './InviteParticipantModal.scss';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../utils/api';
import { CloseIcon } from '../common/CloseIcon';

interface InviteParticipantModalProps {
  conversationId: string;
  pendingInviteEmail?: string | null;
  onClose: () => void;
  refreshConversations?: () => void;
  onRefreshMessages?: () => void;
}

const InviteParticipantModal: React.FC<InviteParticipantModalProps> = ({
  conversationId,
  pendingInviteEmail,
  onClose,
  refreshConversations,
  onRefreshMessages
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const willReplaceInvite = pendingInviteEmail && email.trim().toLowerCase() !== pendingInviteEmail.toLowerCase() && email.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSending(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await apiRequest(`/conversations/${conversationId}/participants`, {
        method: 'POST',
        body: { email: email.trim() },
      });

      if (data.resent) {
        setSuccess(t('messages.invite.successResent', { email: email.trim() }));
      } else if (data.existing) {
        setSuccess(t('messages.invite.successExisting', { email: email.trim() }));
      } else {
        setSuccess(t('messages.invite.successInvite', { email: email.trim() }));
      }

      setEmail('');
      refreshConversations?.();
      onRefreshMessages?.();

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      // Backend returns only error codes (no English text). Map each known code
      // to a translation key; unknown codes fall back to the generic errorFailed.
      const errorCode: string = err?.code || '';
      const codeToKey: Record<string, string> = {
        ALREADY_PARTICIPANT: 'errorAlreadyParticipant',
        ALREADY_INVITED_BY_OTHER: 'errorAlreadyInvited',
        ALREADY_ADDED_PARTICIPANT: 'errorAlreadyAddedParticipant',
        CONVERSATION_ENDED: 'errorConversationEnded',
        NOT_ORIGINAL_PARTICIPANT: 'errorNotOriginalParticipant',
        EMAIL_REQUIRED: 'errorEmailRequired',
        JAAKKO_CONVERSATION: 'errorJaakkoConversation',
      };
      const errorKey = codeToKey[errorCode] || 'errorFailed';
      setError(t(`messages.invite.${errorKey}`));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div className="invite-modal" onClick={e => e.stopPropagation()}>
        <div className="invite-modal__header">
          <h3>{t('messages.invite.title')}</h3>
          <button className="invite-modal__close" onClick={onClose}>
            <CloseIcon size={16} />
          </button>
        </div>

        <p className="invite-modal__description">{t('messages.invite.description')}</p>

        <form onSubmit={handleSubmit} className="invite-modal__form">
          <div className="invite-modal__field">
            <label>{t('messages.invite.emailLabel')}</label>
            <input
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                e.target.setCustomValidity('');
              }}
              onInvalid={e => {
                (e.target as HTMLInputElement).setCustomValidity(
                  t('messages.invite.errorEmailInvalid')
                );
              }}
              placeholder={t('messages.invite.emailPlaceholder')}
              required
              autoFocus
            />
          </div>

          {willReplaceInvite && (
            <div className="invite-modal__warning">
              {t('messages.invite.replaceWarning', { email: pendingInviteEmail })}
            </div>
          )}

          {error && <div className="invite-modal__error">{error}</div>}
          {success && <div className="invite-modal__success">{success}</div>}

          <button
            type="submit"
            className="invite-modal__submit"
            disabled={!email.trim() || isSending}
          >
            {isSending ? t('messages.invite.sending') : t('messages.invite.sendInvite')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteParticipantModal;
